import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { accountMe, logoutAccount, putFavorites, putProfile } from "@/lib/account-api";
import { clearCycle } from "@/lib/cycle";
import { zodiacVisible } from "@/lib/faith-customs";

export const OWNER_KEY = "lunelle-cycle-owner";
const AccountContext = createContext(null);

export const AccountProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [kitchenView, setKitchenView] = useState("week");

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges the session first
    if (window.location.hash?.includes("session_id=")) { setReady(true); return undefined; }
    let live = true;
    accountMe().then((u) => { if (live) setUser(u); }).catch(() => {}).finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, []);

  const signOut = useCallback(async () => {
    try { await logoutAccount(); } catch { /* session already gone */ }
    window.localStorage.removeItem(OWNER_KEY);
    clearCycle();
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const u = await putProfile(patch);
    setUser(u);
    return u;
  }, []);

  // Hearted recipes ("phase-body" ids), saved to the account; returns the new saved state
  const toggleFavorite = useCallback(async (id) => {
    const list = Array.isArray(user?.favorites) ? user.favorites : [];
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    setUser((u) => (u ? { ...u, favorites: next } : u));
    try {
      setUser(await putFavorites(next));
    } catch (e) {
      setUser((u) => (u ? { ...u, favorites: list } : u));
      throw e;
    }
    return next.includes(id);
  }, [user?.favorites]);

  const value = useMemo(() => ({ user, ready, setUser, signOut, updateProfile, toggleFavorite, authMode, openAuth: (m = "signin") => setAuthMode(m), closeAuth: () => setAuthMode(null),
    mergeOpen, setMergeOpen, profileOpen, setProfileOpen, kitchenView, setKitchenView }),
    [user, ready, signOut, updateProfile, toggleFavorite, authMode, mergeOpen, profileOpen, kitchenView]);
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = () => useContext(AccountContext);

// Member's saved faith/sign; `member` is false for signed-out visitors (who see everything, as before)
export const useProfile = () => {
  const ctx = useAccount();
  const profile = ctx?.user?.profile ?? null;
  const member = Boolean(ctx?.user);
  return { member, profile, faith: profile?.faith ?? null, zodiac: profile?.zodiac ?? null,
    showZodiac: member ? zodiacVisible(profile) : true, updateProfile: ctx?.updateProfile, openProfile: () => ctx?.setProfileOpen(true) };
};
